import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect, useDispatch } from "react-redux";
import { Container, Grid, makeStyles } from "@material-ui/core";
import DatePicker from "../../ui_components/date_picker";
import { isValid } from "date-fns";
import { formatDate } from "../../../utils/helper_functions";
import { API_ROUTES } from "../../../constants/api_routes";
import Autocomplete from "../../ui_components/autocomplete";
import {
    changeFilters,
    getCustomersDailyCount,
    getAppointmentsMontlyCountByWorkers,
    getAppointmentsCountByCustomers
} from "../../../store/actions/reports/graph_analytics_action";

import { FormControlLabel, Switch } from "@material-ui/core";

import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";

import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(3, 2),
        maxWidth: 2000
    },
    alignCenter: {
        textAlign: "center"
    }
}));

const setZeroTime = (date) => {
    return date ? new Date(date.setHours(0, 0, 0, 0)) : null;
};

const monthNames = ["Januar", "Februar", "Mart", "April", "Maj", "Jun", "Jul", "Avgust", "Septembar", "Oktobar", "Novembar", "Decembar"];
const rgbCollors = [
    "rgb(255,0,0)",
    "rgb(0,255,255)",
    "rgb(255,128,0)",
    "rgb(0,128,255)",
    "rgb(255,255,0)",
    "rgb(0,0,255)",
    "rgb(128,255,0)",
    "rgb(128,0,255)",
    "rgb(0,175,0)",
    "rgb(255,0,255)",
    "rgb(0,255,128)",
    "rgb(255,0,128)"
];

const GraphAnalytics = ({
    graphAnalytics: {
        filters,
        customersDailyCount,
        customersMonthlyCountByWorkers,
        appointmentsMonthlyCountByWorkers,
        appointmentsCountByCustomers
    }
}) => {
    const { container } = useStyles();
    const dispatch = useDispatch();

    const [gridItemSpan, setGridItemSpan] = useState(12);

    useEffect(() => {
        dispatch(getCustomersDailyCount(filters));
        dispatch(getAppointmentsMontlyCountByWorkers(filters));
        dispatch(getAppointmentsCountByCustomers(filters));
    }, [filters]);

    const handleStartDateChange = async (e) => {
        const startDate = setZeroTime(e);
        if (startDate === null || isValid(startDate)) {
            dispatch(changeFilters({ ...filters, startDate }));
        }
    };

    const handleEndDateChange = async (e) => {
        const endDate = setZeroTime(e);
        if (endDate === null || isValid(endDate)) {
            dispatch(changeFilters({ ...filters, endDate }));
        }
    };

    const handleWorkerObject = async (object) => {
        const worker = object;
        dispatch(changeFilters({ ...filters, worker }));
    };

    const handleCustomerObject = async (object) => {
        const customer = object;
        dispatch(changeFilters({ ...filters, customer }));
    };

    const handleArticleObject = async (object) => {
        const article = object;
        dispatch(changeFilters({ ...filters, article }));
    };

    const handleSubAreaObject = async (object) => {
        const subArea = object;
        dispatch(changeFilters({ ...filters, subArea }));
    };

    const customersDailyCountFormat = (data) => {
        const labels = Object.keys(data).map((item) => formatDate(new Date(item)));
        const dataValues = Object.values(data);
        return {
            labels,
            datasets: [
                {
                    label: "Broj klijenata",
                    data: dataValues,
                    borderColor: "rgb(255, 99, 132)",
                    backgroundColor: "rgb(0,128,255)"
                }
            ]
        };
    };

    const customersMonthlyCountByWorkersFormat = (data) => {
        const labels = [...new Set(data.map((item) => item.worker))];

        const datasets = [...new Set(data.map((item) => item.month))].map((month) => {
            const dataValues = labels.map((worker) => {
                const values = data.filter((item) => item.worker === worker && item.month === month);

                return values.length > 0 ? values.shift().count : 0;
            });

            return {
                label: monthNames[new Date(month + "-01").getMonth()],
                data: dataValues,
                borderColor: rgbCollors[new Date(month + "-01").getMonth()],
                backgroundColor: rgbCollors[new Date(month + "-01").getMonth()]
            };
        });

        return {
            labels,
            datasets
        };
    };

    const appointmentsMonthlyCountByWorkersFormat = (data) => {
        const workersUuids = [...new Set(data.map((item) => item.workerUuid))];

        const datasets = [...new Set(data.map((item) => item.month))].map((month) => {
            const dataValues = workersUuids.map((workerUuid) => {
                const values = data.filter((item) => item.workerUuid === workerUuid && item.month === month);

                return values.length > 0 ? values.shift().count : 0;
            });

            return {
                label: monthNames[new Date(month + "-01").getMonth()],
                data: dataValues,
                borderColor: rgbCollors[new Date(month + "-01").getMonth()],
                backgroundColor: rgbCollors[new Date(month + "-01").getMonth()]
            };
        });
        const labels = workersUuids.map((workerUuid) => data.filter((item) => item.workerUuid === workerUuid).shift().worker);

        return {
            labels,
            datasets
        };
    };

    const appointmentsCountByCustomersFormat = (data) => {
        const labels = data.map((item) => item.customer);
        const dataValues = data.map((item) => item.count);
        return {
            labels,
            datasets: [
                {
                    label: "Broj pregleda",
                    data: dataValues,
                    borderColor: "rgb(0,255,255)",
                    backgroundColor: "rgb(0,255,255)"
                }
            ]
        };
    };

    const countTotal = (data, isObject = false) => {
        return data.reduce((total, appointment) => {
            const countValue = isObject ? appointment : appointment.count;
            return total + countValue;
        }, 0);
    };

    const options = (i) => {
        let titleText = "";
        switch (i) {
            case 1: {
                titleText = "Broj novih klijenata na dnevnom nivou, " + countTotal(Object.values(customersDailyCount), true);
                break;
            }
            case 2: {
                titleText = "Broj novih klijenata po zaposlenom, " + countTotal(customersMonthlyCountByWorkers);
                break;
            }
            case 3: {
                titleText = "Broj svih pregleda po zaposlenom, " + countTotal(appointmentsMonthlyCountByWorkers);
                break;
            }
            case 4: {
                titleText = "Broj svih pregleda po pacijentu, " + countTotal(appointmentsCountByCustomers);
                break;
            }
            default: {
                console.log("");
            }
        }

        return {
            plugins: {
                title: {
                    display: true,
                    text: titleText
                }
            }
        };
    };

    const handleGraphWidth = (e) => {
        setGridItemSpan(e.target.checked ? 12 : 6);
    };

    return (
        <Container className={container}>
            <Grid container item xs={12} justify="center" alignItems="flex-start" spacing={2}>
                <Grid item xs={12} md={4}>
                    <Autocomplete
                        name="worker"
                        label="Radnik (prazno uključuje sve)"
                        placeholder="Radnik"
                        api={API_ROUTES.WORKERS}
                        handleObject={handleWorkerObject}
                        preloadValue={filters?.worker}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <Autocomplete
                        name="customer"
                        label="Klijent (prazno uključuje sve)"
                        placeholder="Klijent"
                        api={API_ROUTES.CUSTOMERS}
                        handleObject={handleCustomerObject}
                        preloadValue={filters?.customer}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <Autocomplete
                        name="article"
                        label="Usluga (prazno uključuje sve)"
                        placeholder="Usluga"
                        api={API_ROUTES.ARTICLES}
                        handleObject={handleArticleObject}
                        preloadValue={filters?.article}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <Autocomplete
                        name="subArea"
                        label="Dijagnoza (prazno uključuje sve)"
                        placeholder="Dijagnoza"
                        api={API_ROUTES.SUB_AREAS}
                        handleObject={handleSubAreaObject}
                        preloadValue={filters?.subArea}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <DatePicker
                        name="startDate"
                        value={filters?.startDate}
                        label="Početak"
                        placeholder="Početak"
                        onChange={handleStartDateChange}
                        readOnly={false}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <DatePicker
                        name="endDate"
                        value={filters?.endDate}
                        label="Kraj"
                        placeholder="Kraj"
                        onChange={handleEndDateChange}
                        readOnly={false}
                    />
                </Grid>
                <Grid item xs={12} md={4} style={{ textAlign: "right", alignSelf: "center" }}>
                    <FormControlLabel
                        style={{ marginLeft: 0 }}
                        control={<Switch color="primary" defaultChecked onChange={handleGraphWidth} />}
                        label="Puna širina za grafike"
                        labelPlacement="start"
                    />
                </Grid>

                <Grid item xs={12} md={gridItemSpan}>
                    <Bar options={options(1)} data={customersDailyCountFormat(customersDailyCount)} key={`${gridItemSpan}_1`} />
                </Grid>
                <Grid item xs={12} md={gridItemSpan}>
                    <Bar
                        options={options(2)}
                        data={customersMonthlyCountByWorkersFormat(customersMonthlyCountByWorkers)}
                        key={`${gridItemSpan}_2`}
                    />
                </Grid>
                <Grid item xs={12} md={gridItemSpan}>
                    <Bar
                        options={options(3)}
                        data={appointmentsMonthlyCountByWorkersFormat(appointmentsMonthlyCountByWorkers)}
                        key={`${gridItemSpan}_3`}
                    />
                </Grid>
                <Grid item xs={12} md={gridItemSpan}>
                    <Bar
                        options={options(4)}
                        data={appointmentsCountByCustomersFormat(appointmentsCountByCustomers)}
                        key={`${gridItemSpan}_4`}
                    />
                </Grid>
            </Grid>
        </Container>
    );
};

GraphAnalytics.propTypes = {
    graphAnalytics: PropTypes.object
};

const mapStateToProps = (state) => ({
    graphAnalytics: state.graphAnalytics
});

export default connect(mapStateToProps)(GraphAnalytics);
